import { useEffect, useRef } from 'react'
import { Animated } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { SvgXml } from 'react-native-svg'

import { useDelayedSpinner } from '../hooks/useDelayedSpinner'

// Exact Figma 2028:5263 export, mirrored so Metro needs no SVG loader. The two
// arcs are one ring: the lead is drawn over the track it runs on.
const SPINNER_SVG =
  '<svg preserveAspectRatio="none" overflow="visible" style="display: block;" width="64.5979" height="64.5979" viewBox="0 0 64.5979 64.5979" fill="none" xmlns="http://www.w3.org/2000/svg">\n<g id="Group 245">\n<path id="Ellipse 7" d="M46.1932 46.1304C43.2217 49.1859 39.3451 51.2032 35.1375 51.8835C30.93 52.5637 26.6152 51.8709 22.8322 49.9075C19.0491 47.9441 15.999 44.8145 14.1335 40.9823C12.2679 37.15 11.6862 32.8189 12.4744 28.6302C13.2626 24.4415 15.3789 20.618 18.5097 17.726C21.6406 14.8339 25.6197 13.0271 29.8576 12.573C34.0955 12.119 38.367 13.0419 42.0395 15.205C45.712 17.3681 48.5902 20.6565 50.2479 24.5831" stroke="#3269F6" stroke-width="6" stroke-linecap="round"/>\n<path id="Ellipse 8" d="M12.4621 32.5753C12.4027 28.3135 13.7175 24.1459 16.2116 20.6897C18.7058 17.2334 22.2467 14.6724 26.3101 13.3857C30.3734 12.099 34.7431 12.1552 38.772 13.5459C42.801 14.9366 46.2749 17.5878 48.6794 21.107C51.0839 24.6262 52.2911 28.8262 52.1222 33.085C51.9533 37.3439 50.4174 41.4351 47.7418 44.7529C45.0661 48.0706 41.3932 50.4385 37.2668 51.5057C33.1404 52.573 28.7799 52.283 24.8312 50.6786" stroke="#3269F6" stroke-opacity="0.3" stroke-width="6" stroke-linecap="round"/>\n</g>\n</svg>\n'

const EXPORT_STROKE = /#3269F6/g
const TRACK_STROKE = 'stroke="#3269F6" stroke-opacity="0.3"'

// One turn every 600ms, keeping the rate swing. Polymarket's US app — the
// reference for this wait — turns every 660ms, but evenly; ours trades an even
// rate for a livelier ring, so it runs a little quicker to carry the swing.
const ROTATION_DURATION = 600
const MODULATION_AMPLITUDE = 0.4

const TAU = 2 * Math.PI

/**
 * Turn progress whose rate swings once per revolution, so the ring reads as
 * alive rather than mechanical. Even across the loop seam and monotonic while
 * the amplitude stays under 1, so the ring neither jumps nor runs backwards.
 */
export function modulatedTurn(t: number): number {
  return t + (MODULATION_AMPLITUDE * Math.sin(TAU * t)) / TAU
}

/**
 * The ring the whole SDK waits behind. Presentational only — the caller owns
 * the accessibility role, since a spinner means different things in a button
 * slot and on a submission screen.
 *
 * `trackColor` replaces the export's own 30% of `color`, for a theme that
 * colors the track itself (`content/button/*\/loadingAlpha`).
 */
export function LoadingSpinner({
  size,
  color,
  trackColor,
  delay = 0,
}: {
  size: number
  color: string
  /** Defaults to the export's 30% of `color`. */
  trackColor?: string
  /** Milliseconds to hold the ring back; a wait that ends sooner draws nothing. */
  delay?: number
}) {
  const visible = useDelayedSpinner(delay)
  const rotation = useRef(new Animated.Value(0)).current
  const reduceMotion = useReducedMotion()
  useEffect(() => {
    if (reduceMotion) {
      return
    }
    const animation = Animated.loop(
      Animated.timing(rotation, {
        toValue: 1,
        duration: ROTATION_DURATION,
        easing: modulatedTurn,
        useNativeDriver: true,
      }),
    )
    animation.start()
    return () => animation.stop()
  }, [rotation, reduceMotion])

  if (!visible) {
    return null
  }

  const tinted =
    trackColor === undefined
      ? SPINNER_SVG
      : SPINNER_SVG.replace(TRACK_STROKE, `stroke="${trackColor}"`)

  return (
    <Animated.View
      style={{
        transform: [
          {
            rotate: rotation.interpolate({
              inputRange: [0, 1],
              outputRange: ['0deg', '360deg'],
            }),
          },
        ],
      }}
    >
      <SvgXml
        xml={tinted.replace(EXPORT_STROKE, color)}
        width={size}
        height={size}
      />
    </Animated.View>
  )
}
